import type { ReactNode } from 'react';

import Box from '@mui/material/Box';

interface GemBadgeProps {
  /** Gem display name — its first character stands in when no `glyph` is given. */
  name: string;
  /** Hex accent color from the API. */
  accent: string;
  /** Filled (accent bg) when the gem is the active one; tinted otherwise. */
  active?: boolean;
  size?: number;
  /** Optional custom glyph (e.g. the general "Sahib" star). */
  glyph?: ReactNode;
}

// Round, accent-tinted gem marker shared by the chat sidebar and composer.
// API gems carry no React glyph, so the gem's initial stands in for one.
export function GemBadge({ name, accent, active = false, size = 22, glyph }: GemBadgeProps) {
  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor: active ? accent : `${accent}26`,
        color: active ? '#fff' : accent,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        fontFamily: '"Cairo Variable", "Cairo", sans-serif',
        fontWeight: 700,
        fontSize: Math.round(size * 0.46),
        lineHeight: 1,
        userSelect: 'none',
      }}
    >
      {glyph ?? firstChar(name)}
    </Box>
  );
}

// Unicode-aware first character (spreads to code points so multi-byte
// Arabic/emoji glyphs aren't split).
function firstChar(name: string): string {
  return [...name.trim()][0]?.toUpperCase() ?? '?';
}
